// =============== Customer Information ===============

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

// =============== Payment Providers & Methods ===============

export type PaymentProvider = 'MercadoPago' | 'Stripe' | 'Paypal' | 'Unknown';
export type PaymentMethod = 'Pix' | 'Card' | 'PayPal' | 'Unknown' | 'None';

export type PaymentSelectionKey = `${PaymentProvider}:${PaymentMethod}`;

export interface PaymentMethodDescriptor {
  provider: PaymentProvider;
  method: PaymentMethod;
  displayName: string;
  description?: string;
}

export interface PaymentSession {
  sessionId: string;
  clientSecret?: string;
  publicKey?: string;
  redirectUrl?: string;
  providerPaymentId?: string;
  metadata?: Record<string, string>;
}

// =============== Voucher Information ===============

export interface VoucherInfo {
  code: string;
  discountType: 'percentage' | 'fixedAmount' | 'freeShipping';
  discountValue: number;
  applied: boolean;
  message?: string;
  discountApplied?: number;
  reservedUntilUtc?: string;
  redemptionStatus?: 'Reserved' | 'Applied' | 'Voided';
}

// =============== Order Entity ===============

export type OrderStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'paid';

export interface Order {
  id: string;
  packageId: string;
  customerInfo: CustomerInfo | null;
  voucher: VoucherInfo | null;
  status: OrderStatus;
  subtotal: number;
  discount: number;
  total: number;
  createdAt: string;
  updatedAt?: string;
}

// =============== Create Order ===============

export interface CreateOrderRequest {
  packageId: string;
}

export interface CreateOrderResponse {
  order: Order;
  success: boolean;
  message?: string;
}

// =============== Apply Voucher ===============

export interface ApplyVoucherRequest {
  orderId: string;
  voucherCode: string;
}

export interface ApplyVoucherResponse {
  success: boolean;
  order?: Order;
  voucher?: VoucherInfo;
  message?: string;
}

// =============== Update Customer Info ===============

export interface UpdateCustomerInfoRequest {
  orderId: string;
  customerInfo: CustomerInfo;
}

export interface UpdateCustomerInfoResponse {
  success: boolean;
  order?: Order;
  message?: string;
}

// =============== Commit Order ===============

export interface CommitOrderRequest {
  orderId: string;
  provider: PaymentProvider;
  method: PaymentMethod;
}

export interface CommitOrderResponse {
  success: boolean;
  requiresPayment?: boolean;
  order?: Order;
  paymentSession?: PaymentSession;
  transactionId?: string;
  message?: string;
}

// =============== Get Order ===============

export interface GetOrderResponse {
  success: boolean;
  order?: Order;
  message?: string;
}

// =============== Process Card Payment (Mercado Pago Brick) ===============

export interface CardPaymentFormData {
  token: string;
  paymentMethodId?: string;
  installments: number;
  issuerId?: string;
  deviceId?: string;
  payer?: {
    email?: string;
    identification?: {
      type?: string;
      number?: string;
    };
  };
}

export interface ProcessCardPaymentResponse {
  success: boolean;
  transientFailure?: boolean;
  message?: string;
  orderStatus?: string;
  paymentStatus?: string;
  providerPaymentId?: string;
}

// =============== Generic Order Response ===============

export interface OrderResponse {
  success: boolean;
  order?: Order;
  message?: string;
}

// =============== Error Response ===============

export interface ErrorResponse {
  message: string;
  errorCode?: string;
}

export const ErrorCodes = {
  PACKAGE_ALREADY_PURCHASED: 'PACKAGE_ALREADY_PURCHASED',
  PACKAGE_PROMOTION_UNAVAILABLE: 'PACKAGE_PROMOTION_UNAVAILABLE',
} as const;

export type CheckoutProcessResult =
  | { status: 'validation_failed' }
  | { status: 'completed' }
  | { status: 'awaiting_payment'; session: PaymentSession }
  | { status: 'failed'; message: string };

export const toPaymentSelectionKey = (
  provider: PaymentProvider,
  method: PaymentMethod
): PaymentSelectionKey => `${provider}:${method}`;

export const parsePaymentSelectionKey = (
  key: string
): { provider: PaymentProvider; method: PaymentMethod } | null => {
  const [provider, method] = key.split(':') as [PaymentProvider, PaymentMethod];
  if (!provider || !method) return null;
  return { provider, method };
};
